import React, { useEffect, useState } from "react";
import { useCartStore } from "../../store/cart.store";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../context/ThemeContext";

export const DynamicCart: React.FC = () => {
  const { items, totalPrice, lastAddedItem, clearLastAdded } = useCartStore();
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const isCool = theme === 'cool';
  const itemCount = items.reduce((acc, item) => acc + item.quantity, 0);

  useEffect(() => {
    if (lastAddedItem) {
      // Avoid wide expansion on narrow phones (overlaps header controls)
      const allowExpand = typeof window === "undefined" || window.innerWidth >= 640;
      if (allowExpand) setIsExpanded(true);
      const timer = setTimeout(() => {
        setIsExpanded(false);
        clearLastAdded(); 
      }, allowExpand ? 3000 : 1200); 
      return () => clearTimeout(timer);
    }
  }, [lastAddedItem, clearLastAdded]);

  // CHANGED: Removed the 'if (itemCount === 0) return null;' check.
  // Now the cart is always visible, even if empty.

  return (
    <Link to="/cart">
      <motion.div
        layout
        initial={false}
        animate={{
          width: isExpanded ? "auto" : 48, 
          borderRadius: 24, 
        }}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
        className={`relative h-11 w-11 sm:h-12 sm:w-auto min-h-[44px] min-w-[44px] flex items-center justify-center overflow-hidden shadow-lg border backdrop-blur-md cursor-pointer shrink-0 ${
          isExpanded 
            ? (isCool ? "bg-cyan-900/90 border-cyan-500 text-cyan-50 px-3 sm:px-4 max-w-[140px] sm:max-w-none" : "bg-orange-900/90 border-orange-500 text-white px-3 sm:px-4 max-w-[140px] sm:max-w-none")
            : (isCool 
                ? `bg-slate-800/80 border-cyan-900/50 text-cyan-400 ${itemCount === 0 ? 'opacity-60 hover:opacity-100' : 'opacity-100'}` 
                : `bg-white/80 border-orange-200 text-orange-700 ${itemCount === 0 ? 'opacity-60 hover:opacity-100' : 'opacity-100'}`)
        }`}
      >
        <AnimatePresence mode="wait">
          {isExpanded ? (
            <motion.div
              key="expanded"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="hidden sm:flex items-center gap-3 whitespace-nowrap min-w-[200px]"
            >
              <div className="flex flex-col leading-none">
                <span className="text-[10px] uppercase tracking-wider opacity-70">Added</span>
                <span className="font-bold text-sm truncate max-w-[120px]">{lastAddedItem?.name}</span>
              </div>

              <div className={`px-2 py-1 rounded-full text-xs font-bold ${isCool ? "bg-cyan-500 text-slate-900" : "bg-orange-500 text-white"}`}>
                + {lastAddedItem?.quantity}
              </div>

              <div className="h-6 w-[1px] bg-current opacity-20 mx-1"></div>

              <div className="font-mono font-bold text-sm">
                ₹{totalPrice()}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="collapsed"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              className="relative"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
              
              {/* CHANGED: Only show red badge if itemCount > 0 */}
              {itemCount > 0 && (
                <span className={`absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold shadow-sm ${
                   isCool ? "bg-cyan-500 text-slate-900" : "bg-orange-600 text-white"
                }`}>
                  {itemCount}
                </span>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </Link>
  );
};